import type { MisconceptionDefinition } from '../types'

/**
 * TAA — Woordenschat sub-registry.
 *
 * Misconcepties rond synoniemen/antoniemen, contextbetekenis en
 * figuurlijk taalgebruik (idiomen). Families: `TAA-WOORD-SYNONIEM`,
 * `TAA-WOORD-CONTEXT`, `TAA-WOORD-IDIOOM`, `TAA-WOORD-ANTONIEM`.
 *
 * Onderdeel van de TAA-barrel-splitsing (juni 2026).
 */
export const TAA_WOORD_MISCONCEPTIONS: MisconceptionDefinition[] = [
  {
    id: 'taa.antonym-as-synonym',
    label: 'Verwart antoniem met synoniem',
    module: 'Taalbegrip',
    description:
      'Leerling kiest het tegenovergestelde woord in plaats van een synoniem.',
    typicalFamilies: ['TAA-WOORD-SYNONIEM'],
  },
  {
    id: 'taa.related-word-as-synonym',
    label: 'Verwart verwant woord met synoniem',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een woord uit hetzelfde thema maar met een andere betekenis.',
    typicalFamilies: ['TAA-WOORD-SYNONIEM'],
  },
  {
    id: 'taa.near-synonym-not-synonym',
    label: 'Denkt dat bijna-synoniem geen synoniem is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat woorden met een iets andere nuance geen synoniemen zijn.',
    typicalFamilies: ['TAA-WOORD-SYNONIEM'],
  },
  {
    id: 'taa.strong-synonym-not-synonym',
    label: 'Denkt dat sterk synoniem geen synoniem is',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat woorden met een sterkere betekenis geen synoniemen zijn.',
    typicalFamilies: ['TAA-WOORD-SYNONIEM'],
  },
  {
    id: 'taa.positive-word-in-negative-context',
    label: 'Kiest positief woord bij negatieve context',
    module: 'Taalbegrip',
    description:
      'Leerling negeert negatieve contextaanwijzingen en kiest een positief woord.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.underestimate-context-clue',
    label: 'Negeert contextaanwijzing',
    module: 'Taalbegrip',
    description:
      'Leerling mist een belangrijke contextaanwijzing (bijv. "verbaasd") die de betekenis bepaalt.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.opposite-meaning-context',
    label: 'Kiest tegenovergestelde betekenis',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een woord met de tegenovergestelde betekenis van wat de context aangeeft.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.wrong-dimension-context',
    label: 'Verwart dimensie bij contextbetekenis',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een woord uit een verkeerde dimensie (bijv. snelheid i.p.v. hoeveelheid).',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.wrong-action-context',
    label: 'Kiest verkeerde handeling bij context',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een handeling die niet past bij de beschreven context.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.neutral-word-for-emotional',
    label: 'Kiest neutraal woord bij emotionele context',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een neutraal woord terwijl de context een emotionele situatie beschrijdt.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.idioom-letterlijk-cadeau',
    label: 'Neemt uitdrukking letterlijk (cadeau)',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "hart onder de riem steken" als cadeau geven.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-riem',
    label: 'Neemt uitdrukking letterlijk (riem)',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "hart onder de riem steken" als riem lenen.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-steken',
    label: 'Neemt steken letterlijk (pijn)',
    module: 'Taalbegrip',
    description: 'Leerling interpreteert "steken" als pijn doen.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-buikpijn',
    label: 'Neemt uitdrukking letterlijk (buikpijn)',
    module: 'Taalbegrip',
    description: 'Leerling interpreteert "vlinders in je buik" als buikpijn.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-vlinders',
    label: 'Neemt uitdrukking letterlijk (echte vlinders)',
    module: 'Taalbegrip',
    description: 'Leerling denkt dat er echte vlinders zijn gezien.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-honger',
    label: 'Verwart buikgevoel met honger',
    module: 'Taalbegrip',
    description: 'Leerling interpreteert "vlinders in je buik" als honger.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-verwisseld-boter',
    label: 'Verwart neus-in-boter met hard werken',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "met je neus in de boter vallen" (geluk) met hard werken.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-verwisseld-spijker',
    label: 'Verwart spijker-op-kop met hard werken',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "de spijker op de kop slaan" (goed raden) met hard werken.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-verwisseld-mand',
    label: 'Verwart door-de-mand met hard werken',
    module: 'Taalbegrip',
    description:
      'Leerling verwart "door de mand vallen" (betrapt worden) met hard werken.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-olifant-sterk',
    label: 'Neemt olifant letterlijk (sterk)',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "van een mug een olifant maken" als sterk zijn.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-olifant-slim',
    label: 'Interpreteert mug-olifant als slim',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "van een mug een olifant maken" als slim zijn.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-letterlijk-olifant-luid',
    label: 'Interpreteert mug-olifant als luidruchtig',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert "van een mug een olifant maken" als luidruchtig zijn.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-context-parafrase-fout',
    label: 'Context-parafrase van idioom fout',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een parafrase die context-betekenis van een idioom verkeerd weergeeft (bv. geruststelling vs aanmoediging).',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-context-oorzaak-fout',
    label: 'Context-oorzaak van idioom fout geïnterpreteerd',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert de oorzaak/aanleiding bij een figuurlijke uitdrukking verkeerd (bv. zenuwen verward met ziekte).',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-transfer-mist-patroon',
    label: 'Transfer-patroon van idioom gemist',
    module: 'Taalbegrip',
    description:
      'Leerling mist het overdrijvings-/figuurlijke patroon van een idioom en verwart het met een feit of relativering.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-fill-letterlijk-gekozen',
    label: 'Letterlijke omschrijving gekozen i.p.v. idioom',
    module: 'Taalbegrip',
    description:
      'Leerling typt of kiest een letterlijke omschrijving in plaats van het figuurlijke idioom.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  // ─── Antoniem — seed-reconciliatie (Phase B, juni 2026) ─────────────────
  {
    id: 'taa.antoniem-synoniem-verward',
    label: 'Antoniem verward met synoniem (specifiek)',
    module: 'Taalbegrip',
    description:
      'Leerling kiest bij een antoniem-vraag juist een synoniem (bijv. "moedig" als tegenovergestelde van "dapper").',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.antoniem-ander-thema-sterk',
    label: 'Ander thema gekozen (kracht/sterk)',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een woord uit een ander semantisch thema (kracht/sterk) i.p.v. een antoniem.',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.antoniem-ander-thema-slim',
    label: 'Ander thema gekozen (intelligentie/slim)',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een woord uit een ander semantisch thema (intelligentie/slim) i.p.v. een antoniem.',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.antoniem-ander-thema-kort',
    label: 'Ander thema gekozen (lengte/kort)',
    module: 'Taalbegrip',
    description:
      'Leerling verwart duur met lengte en kiest "kort" als antoniem in een vraag over tijdsduur.',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.antoniem-ander-thema-snel',
    label: 'Ander thema gekozen (snelheid/snel)',
    module: 'Taalbegrip',
    description:
      'Leerling verwart duur met snelheid en kiest "snel" als antoniem.',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.antoniem-ander-thema-langzaam',
    label: 'Ander thema gekozen (snelheid/langzaam)',
    module: 'Taalbegrip',
    description:
      'Leerling verwart duur met snelheid en kiest "langzaam" als antoniem.',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.niet-vraag-somber',
    label: 'GEEN-vraag verkeerd gelezen (somber)',
    module: 'Taalbegrip',
    description:
      'Leerling leest de "welk woord is GEEN synoniem"-vraag verkeerd en kiest een woord dat juist wel synoniem is (somber).',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.niet-vraag-treurig',
    label: 'GEEN-vraag verkeerd gelezen (treurig)',
    module: 'Taalbegrip',
    description:
      'Leerling leest de GEEN-vraag verkeerd en kiest "treurig" terwijl dat juist wel een synoniem is.',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.niet-vraag-verdrietig',
    label: 'GEEN-vraag verkeerd gelezen (verdrietig)',
    module: 'Taalbegrip',
    description:
      'Leerling leest de GEEN-vraag verkeerd en kiest "verdrietig" terwijl dat juist wel een synoniem is.',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.geen-antoniem-warm-koud',
    label: 'Denkt warm-koud geen antoniemen',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat warm en koud geen antoniemen zijn (mist het temperatuur-tegenstellingspaar).',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.geen-antoniem-groot-klein',
    label: 'Denkt groot-klein geen antoniemen',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat groot en klein geen antoniemen zijn (mist het grootte-tegenstellingspaar).',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  {
    id: 'taa.geen-antoniem-licht-donker',
    label: 'Denkt licht-donker geen antoniemen',
    module: 'Taalbegrip',
    description:
      'Leerling denkt dat licht en donker geen antoniemen zijn (mist het licht-tegenstellingspaar).',
    typicalFamilies: ['TAA-WOORD-ANTONIEM'],
  },
  // ─── Idioom — seed-reconciliatie (Phase B, juni 2026) ───────────────────
  {
    id: 'taa.idioom-letterlijk-bijltje',
    label: 'Idioom "bijltje erbij neergooien" letterlijk genomen',
    module: 'Taalbegrip',
    description:
      'Leerling neemt het idioom "het bijltje erbij neergooien" letterlijk in plaats van figuurlijk (opgeven).',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  // ─── Distractor-tag-diversiteit (S-WOORD-02, juni 2026) ─────────────────
  // Splitst eerder gecollapseerde tags op zodat elke distractor zijn eigen
  // diagnostische signaal heeft. Voorheen deelden 2–3 distractors per vraag
  // één misconception-id (bv. taa.idioom-context-parafrase-fout drie keer
  // achter elkaar bij IDIOOM-001) waardoor het misconception-loop systeem
  // geen onderscheid kon maken tussen "geruststelling verward met
  // aanmoediging" en "gebaar verward met gesproken aanmoediging".
  {
    id: 'taa.context-temperatuur-vs-droogte',
    label: 'Verwart temperatuur met droogte in context',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een temperatuur-omschrijving ("donker en koud") terwijl de context juist droogte/onvruchtbaarheid beschrijft.',
    typicalFamilies: ['TAA-WOORD-CONTEXT'],
  },
  {
    id: 'taa.idioom-geruststelling-vs-aanmoediging',
    label: 'Geruststelling verward met aanmoediging',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een geruststellende zin ("maak je geen zorgen") als parafrase van een aanmoedigend idioom — mist het verschil tussen rust geven en moed inspreken.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-reactie-vs-aanmoediging',
    label: 'Reactie van ontvanger verward met aanmoediging',
    module: 'Taalbegrip',
    description:
      'Leerling kiest de reactie van de ontvanger ("dank je, dat helpt") in plaats van de aanmoediging zelf — verwart effect met oorzaak.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-gebaar-vs-gesproken',
    label: 'Gebaar verward met gesproken aanmoediging',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een non-verbaal gebaar (duim opsteken) waar het idioom specifiek over gesproken aanmoediging gaat.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-context-emotie-omgewisseld',
    label: 'Emotie in context omgewisseld (blij vs zenuwen)',
    module: 'Taalbegrip',
    description:
      'Leerling negeert expliciete context-aanwijzingen ("ik ben zo zenuwachtig") en kiest een tegengestelde emotie zoals blijdschap.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-zenuwen-vs-ziekte',
    label: 'Zenuwsymptomen verward met ziektesymptomen',
    module: 'Taalbegrip',
    description:
      'Leerling interpreteert lichamelijke zenuwreacties (trillende handen, snel hart) als ziekte in plaats van als zenuwen.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-transfer-feit-verward',
    label: 'Feit verward met overdrijving',
    module: 'Taalbegrip',
    description:
      'Leerling herkent het overdrijvings-patroon niet en kiest een gewoon feit in plaats van de overdreven uitspraak.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-transfer-advies-omgewisseld',
    label: 'Advies-tegen-overdrijven verward met overdrijving zelf',
    module: 'Taalbegrip',
    description:
      'Leerling verwart de meta-uitspraak ("maak het niet groter dan het is") met een voorbeeld van overdrijven — kiest het advies tegen het patroon i.p.v. het patroon zelf.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
  {
    id: 'taa.idioom-transfer-relativering-verward',
    label: 'Relativering verward met overdrijving',
    module: 'Taalbegrip',
    description:
      'Leerling kiest een relativerende reactie ("een 7 is toch goed?") als voorbeeld van overdrijven — mist dat een relativering juist het tegenovergestelde patroon is.',
    typicalFamilies: ['TAA-WOORD-IDIOOM'],
  },
]
